/**
 * Brain Server API client — all calls go through CF Functions proxy at /api/brain/*.
 */

import type { BrainSession, WSConfig, DailyCosts, WorkOrderFilters, CreateWorkOrderData } from '../types/brain'
import type { WorkOrder } from '../types/work-orders'

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Request failed (${response.status})`)
  }
  return response.json()
}

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_token=([^;]+)/)
  return match ? match[1] : ''
}

function mutationHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

// --- WebSocket Config ---

export async function getWSConfig(): Promise<WSConfig> {
  const res = await fetch('/api/brain/ws-config', { credentials: 'include' })
  return handleResponse<WSConfig>(res)
}

// --- Sessions ---

export async function fetchSessions(limit = 20): Promise<BrainSession[]> {
  const res = await fetch(`/api/brain/sessions?limit=${limit}`, { credentials: 'include' })
  return handleResponse<BrainSession[]>(res)
}

// --- Work Orders (via brain server) ---

export async function fetchBrainWorkOrders(filters?: WorkOrderFilters): Promise<WorkOrder[]> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.to) params.set('to', filters.to)
  if (filters?.from) params.set('from', filters.from)
  const qs = params.toString()
  const res = await fetch(`/api/brain/work-orders${qs ? '?' + qs : ''}`, { credentials: 'include' })
  return handleResponse<WorkOrder[]>(res)
}

export async function fetchBrainWorkOrder(id: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}`, { credentials: 'include' })
  return handleResponse<WorkOrder>(res)
}

export async function createBrainWorkOrder(data: CreateWorkOrderData): Promise<WorkOrder> {
  const res = await fetch('/api/brain/work-orders', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<WorkOrder>(res)
}

export interface AudioToWorkOrderResult {
  success: boolean
  created: boolean
  degraded?: boolean
  reason?: string
  transcriptPreview?: string
  processingErrors?: Array<{
    stage: 'transcription' | 'classification'
    message: string
  }>
  classification?: {
    category: 'WORK' | 'PERSONAL' | 'MIXED'
    confidence: number
    summary: string
    suggestedTitle: string
    ideas: string[]
    topics: string[]
  }
  workOrder?: WorkOrder
}

export async function createBrainWorkOrderFromAudio(input: {
  audio: File
  to: string
  from?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
}): Promise<AudioToWorkOrderResult> {
  const form = new FormData()
  form.append('audio', input.audio, input.audio.name)
  form.append('to', input.to)
  form.append('from', input.from || 'founder')
  form.append('priority', input.priority || 'medium')

  const res = await fetch('/api/brain/work-orders/from-audio', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
    },
    body: form,
  })
  return handleResponse<AudioToWorkOrderResult>(res)
}

export async function approveBrainWorkOrder(id: string, notes?: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ notes }),
  })
  return handleResponse<WorkOrder>(res)
}

export async function rejectBrainWorkOrder(id: string, reason?: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ reason }),
  })
  return handleResponse<WorkOrder>(res)
}

// --- Costs ---

export async function fetchDailyCosts(): Promise<DailyCosts> {
  const res = await fetch('/api/brain/costs', { credentials: 'include' })
  return handleResponse<DailyCosts>(res)
}
